import { queryOptions, useQuery } from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";
import { projectViewerProfileToCompatibility } from "@wags/matchmaker/pure.ts";
import { resolveExistingViewerRef } from "@/lib/core/auth";
import { getViewer } from "@/lib/core/viewer-core.server";
import type IViewerCompatibilitySource from "./viewer-compatibility-source.interface.ts";
import { parseViewerCompatibilityResponse } from "./viewer-compatibility-response.ts";

export const VIEWER_COMPATIBILITY_QUERY_KEY = ["viewer-compatibility"] as const;

export async function readViewerCompatibility(
    source: IViewerCompatibilitySource,
) {
    let ref: Awaited<ReturnType<IViewerCompatibilitySource["resolveRef"]>>;
    try {
        ref = await source.resolveRef();
    } catch {
        return {};
    }
    if (!ref) return {};

    let profile: Awaited<ReturnType<IViewerCompatibilitySource["readProfile"]>>;
    try {
        profile = await source.readProfile(ref);
    } catch {
        return {};
    }

    try {
        return parseViewerCompatibilityResponse(
            projectViewerProfileToCompatibility(profile),
        );
    } catch {
        return {};
    }
}

export const getViewerCompatibility = createServerFn({
    method: "GET",
}).handler(() =>
    readViewerCompatibility({
        resolveRef: () => resolveExistingViewerRef(),
        readProfile: async (ref) => {
            const viewer = await getViewer();
            return viewer.as(ref).profile.getProfile();
        },
    }),
);

export function viewerCompatibilityQueryOptions(enabled = true) {
    return queryOptions({
        queryKey: VIEWER_COMPATIBILITY_QUERY_KEY,
        queryFn: async () =>
            parseViewerCompatibilityResponse(await getViewerCompatibility()),
        enabled,

        staleTime: 0,
        retry: false,
        refetchOnWindowFocus: false,
    });
}

export function useViewerCompatibility(enabled = true) {
    return useQuery(viewerCompatibilityQueryOptions(enabled));
}
